/*global chrome*/
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import NavAnchor from "../../Tags/NavAnchor";
import Image from "../../Tags/Image";
import { ChevronLeft, ChevronLeftCircle, ChevronRightCircle, ExternalLink } from "lucide-react";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";
import usePageAttributes from "../../../hooks/usePageAttributes";
import SharedDataContext from "../../../SharedDataContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTheme } from "../../ThemeProvider";
import WorkspaceDropdown from "../../WorkspaceDropdown";
import { useMutation } from "react-query";
import { setActiveWorkspace } from "../../../apis/user";
import { Button } from "../../ui/button";
import ProfileDropdown from "../../ProfileDropdown";
import { CaretLeftIcon, CaretRightIcon, CaretUpIcon } from "@radix-ui/react-icons";
import Text from "../../Tags/Text";
import InvitationsCard from "../../InvitationsCard";
import { useStoreWorkspaces } from "../../../stores/useStoreWorkspaces";
import { motion } from "framer-motion";
import TooltipWrapper from "../../TooltipWrapper";
import { getExtensionId } from "../../../axios/constant";
export default function LeftNav() {
  const { pendingInvitations } = useStoreWorkspaces();
  const menus = useMemo(
    () => [
      {
        name: "Home",
        id: "dashboard",
        href: "/",
        icon: "/images/icons/untitledui/home.svg",
        activePaths: ["/"],
        hasSubMenu: false,
      },
      {
        name: "Enrich",
        id: "enrich",
        title: "Enrichment",
        href: "/enrich/list",
        icon: "/images/icons/untitledui/magic-wand.svg",
        hasSubMenu: true,
        activePaths: [
          "/enrich/list",
          "/enrich/list/*",
          "/enrich/people",
          // "/enrich/company"
        ],
        subMenu: [
          {
            name: "People",
            href: "/enrich/people",
            icon: "/images/icons/untitledui/user-square.svg",
            activePaths: ["/enrich/people", "/enrich/people/*"],
          },
          // {
          //   name: "Companies",
          //   href: "/enrich/company",
          //   icon: "/images/icons/untitledui/building.svg",
          //   activePaths: ["/enrich/company", "/enrich/company/*"],
          // },
          {
            name: "CSV Enrichment",
            href: "/enrich/list",
            icon: "/images/icons/untitledui/table.svg",
            activePaths: ["/enrich/list", "/enrich/list/*"],
          },
        ],
      },
      {
        name: "Workspace Settings",
        id: "settings",
        title: "Workspace Settings",
        href: "/settings/general",
        icon: "/images/icons/untitledui/settings2.svg",
        activePaths: [
          "/settings/general",
          "/settings/members",
          "/settings/members/requests",
          "/settings/members/invitations",
          "/settings/billing/usage",
          "/settings/billing/plans",
          "/settings/billing/subscriptions",
          "/settings/teams",
          "/settings/teams/*",
          "/settings/integrations",
          "/settings/security",
          "/settings/api-keys",
          "/settings/usage-analytics",
          "/intelligence/scoring",
          "/intelligence/scoring/persona",
          "/intelligence/scoring/custom-fields",
          "/dialer-ai",
          "/dialer-ai/add-user",
          "/numbers-inventory",
          "/numbers-inventory/*",
        ],
        hasSubMenu: true,
        subMenu: [
          {
            name: "Workspace",
            href: "/settings/general",
            icon: "/images/icons/untitledui/workspace.svg",
            activePaths: ["/settings/general"],
          },
          {
            name: "Members",
            href: "/settings/members",
            icon: "/images/icons/untitledui/user.svg",
            activePaths: ["/settings/members", "/settings/members/requests", "/settings/members/invitations"],
          },
          {
            name: "Billing",
            href: "/settings/billing/plans",
            icon: "/images/icons/untitledui/billing.svg",
            activePaths: [
              "/settings/billing/usage",
              "/settings/billing/plans",
              "/settings/billing/subscriptions",
            ],
          },
          {
            name: "Teams",
            href: "/settings/teams",
            icon: "/images/icons/untitledui/group.svg",
            activePaths: ["/settings/teams", "/settings/teams/*"],
          },
          {
            name: "Integrations",
            href: "/settings/integrations?tab=integrations&category=View all",
            icon: "/images/icons/untitledui/toggle.svg",
            activePaths: ["/settings/integrations"],
          },
          {
            name: "Security",
            href: "/settings/security",
            icon: "/images/icons/untitledui/safe.svg",
            activePaths: ["/settings/security"],
          },
          {
            name: "API Keys",
            href: "/settings/api-keys",
            icon: "/images/icons/untitledui/key.svg",
            activePaths: ["/settings/api-keys"],
          },
          {
            name: "Usage Analytics",
            href: "/settings/usage-analytics",
            icon: "/images/icons/untitledui/analytics.svg",
            activePaths: ["/settings/usage-analytics"],
          },

          {
            sectionTitle: "Intelligence",
            items: [
              {
                name: "Scoring",
                href: "/intelligence/scoring",
                icon: "/images/icons/untitledui/scoring.svg",
                activePaths: ["/intelligence/scoring", "/intelligence/scoring/persona"],
              },
              // {
              //   name: "Custom fields",
              //   href: "/intelligence/scoring/custom-fields",
              //   icon: "/images/icons/untitledui/custom-fields.svg",
              //   activePaths: ["/intelligence/scoring/custom-fields"],
              // },
            ],
          },
          {
            sectionTitle: "Dialer",
            items: [
              {
                name: "User access",
                href: "/dialer-ai",
                icon: "/images/icons/untitledui/number-inventory/user-access.svg",
                activePaths: ["/dialer-ai", "/dialer-ai/add-user"],
              },
              {
                name: "Phone numbers",
                href: "/numbers-inventory",
                icon: "/images/icons/untitledui/number-inventory/phone-numbers.svg",
                activePaths: ["/numbers-inventory", "/numbers-inventory/*"],
              },
            ],
          },
        ],
      },
      // {
      //   name: "People",
      //   id: "people",
      //   href: "/contacts",
      //   icon: "/images/icons/untitledui/users.svg",
      //   activePaths: ["/contacts"],
      // },
      // {
      //   name: "Companies",
      //   id: "company",
      //   href: "/companies",
      //   icon: "/images/icons/untitledui/building.svg",
      //   activePaths: ["/companies"],
      // },
      {
        name: "Telephony",
        id: "telephony",
        title: "Telephony",
        href: "/call-logs",
        icon: "/images/icons/untitledui/call-logs/dialer-icon.svg",
        hasSubMenu: true,
        activePaths: ["/call-logs", "/call-logs/*", "/dialer", "/coach-ai", "/activity"],
        subMenu: [
          {
            name: "Dialer",
            href: "/dialer",
            icon: "/images/icons/untitledui/call-logs/dialer-tab-icon.svg",
            activePaths: ["/dialer"],
          },
          {
            name: "Call Recordings",
            href: "/call-logs",
            icon: "/images/icons/untitledui/call-logs/call-log-icon-navbar.svg",
            activePaths: ["/call-logs", "/call-logs/*"],
          },
          {
            name: "Coach AI",
            href: "/coach-ai",
            icon: "/images/coach-ai.svg",
            activePaths: ["/coach-ai"],
          },
          {
            name: "Activity",
            href: "/activity",
            icon: "/images/icons/untitledui/call-activity/Activity-icon.svg",
            activePaths: ["/activity"],
          },
        ],
      },
    ],
    []
  );

  const captureEvent = useCaptureEvent();
  const { logo, appname, favicon, setTitle } = usePageAttributes();
  const userData = useContext(SharedDataContext);
  const { workspaceSlug } = useParams();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  const [sidebarMini, setSidebarMini] = useState(() => {
    // Initialize state from local storage or fallback to false
    const savedState = localStorage.getItem("sidebarMini");
    return savedState !== null ? JSON.parse(savedState) : false;
  });

  const toggleSidebar = () => {
    setSidebarMini((prevSidebarMini) => !prevSidebarMini);
  };

  // Update local storage whenever sidebarMini changes
  useEffect(() => {
    localStorage.setItem("sidebarMini", JSON.stringify(sidebarMini));
  }, [sidebarMini]);

  const replaceAppPathSegment = (newSegment) => {
    if (window.location.pathname.includes("/company-contact-list/push-to-crm")) {
      return `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_APP_HOME}/${workspaceSlug}`;
    }
    const url = new URL(window.location.href);
    const pathParts = url.pathname.split("/");

    const appIndex = pathParts.indexOf(`${process.env.REACT_APP_APP_HOME}`);
    if (appIndex !== -1 && pathParts.length > appIndex + 1) {
      console.log("workspaceSlug", { workspaceSlug, pathPartsSug: pathParts[appIndex + 1], newSegment });
      if (workspaceSlug) {
        pathParts[appIndex + 1] = newSegment;
      }
    }

    url.pathname = pathParts.join("/");

    return url.toString();
  };

  const setActiveWorkspaceFn = useMutation(setActiveWorkspace, {
    onSuccess(data, variables, context) {
      window.location.href = replaceAppPathSegment(data?.user?.activeWorkspace?.slug);
    },
  });

  const switchWorkspace = (id) => {
    setActiveWorkspaceFn.mutate(id);
  };

  const buildWorkspacePath = useCallback(
    (path = "") => {
      const slug = userData?.activeWorkspace?.slug ? `/${userData.activeWorkspace.slug}` : "";
      const normalizedPath = path?.startsWith("/") ? path : `/${path || ""}`;
      return `${slug}${normalizedPath}`.replace(/\/{2,}/g, "/");
    },
    [userData?.activeWorkspace?.slug]
  );

  useEffect(() => {
    const eventListener = (e) => {
      if (workspaceSlug) {
        if (e.detail?.activeWorkspace?.slug !== workspaceSlug) {
          if (e.detail?.workspaces && e.detail?.workspaces.length > 0) {
            const matchingWorkspace = e.detail?.workspaces.find(
              (workspace) => workspace.slug === workspaceSlug
            );
            if (matchingWorkspace && matchingWorkspace.id) {
              switchWorkspace(matchingWorkspace.id);
            } else {
              navigate(`/${workspaceSlug}/404?activeWorkspace=${e.detail?.activeWorkspace?.slug}`);
            }
          }
        }
      }
    };
    window.addEventListener("user_workspace", eventListener);
    return () => {
      window.removeEventListener("user_workspace", eventListener);
    };
  }, [workspaceSlug, navigate]);

  const activeClassNames =
    "sidebar__item__menu sidebar__item__menu__active text-base bg-brand-50 text-brand-700 my-1 group relative flex items-center rounded-lg py-2 px-3 leading-6 hover:bg-brand-100";

  const location = useLocation();

  const matchesActivePath = (pathname, pattern) => {
    if (!pathname || !pattern) {
      return false;
    }

    const normalizedPath =
      pattern.includes("*") || pathname === "/" ? pathname : pathname.replace(/\/+$/, "");
    const escapedPattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const wildcardPattern = escapedPattern.replace(/\\\*/g, ".*");
    const regex = new RegExp(`${wildcardPattern}$`);

    return regex.test(normalizedPath);
  };

  const findRootMenu = (menus, pathname) => {
    for (const menu of menus) {
      const paths = menu.activePaths || [];

      // Check if any activePath matches the pathname (without worrying about subpaths)
      if (paths.some((path) => matchesActivePath(pathname, path))) {
        return {
          hasSubMenu: menu.hasSubMenu,
          subMenu: menu?.subMenu,
          title: menu?.title,
          id: menu?.id,
          name: menu?.name,
        };
      }

      // If menu has subMenu, check subMenu's activePaths too
      // if (menu.subMenu) {
      //   for (const sub of menu.subMenu) {
      //     const subPaths = sub.activePaths || [];
      //     if (subPaths.some((path) => pathname.endsWith(path.replace("*", "")))) {
      //       return menu.id;
      //     }
      //   }
      // }
    }

    return null; // Not found
  };

  const rootMenu = useMemo(() => findRootMenu(menus, location.pathname), [menus, location.pathname]);

  const openWorkspaceSettingsMenu = useCallback(() => {
    const settingsMenu = menus.find((menu) => menu.id === "settings");
    if (!settingsMenu) {
      return;
    }
    setHasSubMenu(true);
    setSubMenus(settingsMenu.subMenu);
    setCurrentMenuTitle(settingsMenu.title || settingsMenu.name);
    setMenuId(settingsMenu.id);
    setOpenSubMenus({ [settingsMenu.id]: true });
    if (!location.pathname.endsWith(settingsMenu.href)) {
      navigate(buildWorkspacePath(settingsMenu.href));
    }
  }, [menus, buildWorkspacePath, navigate, location.pathname]);

  const [openSubMenus, setOpenSubMenus] = useState(() =>
    rootMenu?.hasSubMenu ? { [rootMenu.id]: true } : {}
  );
  const [subMenus, setSubMenus] = useState(rootMenu?.subMenu || []);
  const [hasSubMenu, setHasSubMenu] = useState(Boolean(rootMenu?.hasSubMenu));
  const [currentMenuTitle, setCurrentMenuTitle] = useState(rootMenu?.title || "");
  const [menuId, setMenuId] = useState(rootMenu?.id || "");
  useEffect(() => {
    console.log("location", location.pathname);
    console.log("rootMenu", rootMenu); // 👉 "settings"
    setHasSubMenu(rootMenu?.hasSubMenu);
    setSubMenus(rootMenu?.subMenu);
    setCurrentMenuTitle(rootMenu?.title);
    setMenuId(rootMenu?.id);
    if (rootMenu?.hasSubMenu) {
      setOpenSubMenus((prev) => {
        if (prev[rootMenu.id] && Object.keys(prev).length === 1) {
          return prev;
        }
        return { [rootMenu.id]: true };
      });
    } else {
      setOpenSubMenus((prev) => (Object.keys(prev).length === 0 ? prev : {}));
    }
  }, [rootMenu, location.pathname]);

  useEffect(() => {
    const handleOpenWorkspaceSettingsMenu = () => {
      openWorkspaceSettingsMenu();
    };
    window.addEventListener("openWorkspaceSettingsMenu", handleOpenWorkspaceSettingsMenu);
    return () => {
      window.removeEventListener("openWorkspaceSettingsMenu", handleOpenWorkspaceSettingsMenu);
    };
  }, [openWorkspaceSettingsMenu]);

  const returnActiveClasses = (pathname, id) => {
    if (id === "dashboard" && pathname) {
      if (pathname === `/${workspaceSlug}` || pathname === `/${workspaceSlug}/`) {
        return activeClassNames;
      } else {
        return "sidebar__item__menu text-base text-text-700 my-1 group relative flex items-center rounded-lg py-2 px-3 leading-6 hover:bg-secondary";
      }
    } else {
      return "sidebar__item__menu text-base text-text-700 my-1 group relative flex items-center rounded-lg py-2 px-3 leading-6 hover:bg-secondary";
    }
  };

  const toggleSubMenu = (id) => {
    setOpenSubMenus((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const [isExtensionInstalled, setIsExtensionInstalled] = useState(null); // null until we know
  const [isExtensionHovered, setIsExtensionHovered] = useState(false);
  useEffect(() => {
    try {
      chrome.runtime.sendMessage(
        getExtensionId(), // your extension ID
        { action: "PING" },
        (response) => {
          if (chrome.runtime.lastError) {
            console.log("Extension not available:", chrome.runtime.lastError.message);
            setIsExtensionInstalled(false);
          } else {
            console.log("Extension responded:", response);
            setIsExtensionInstalled(true);
          }
        }
      );
    } catch (err) {
      console.error("Extension communication failed:", err);
      setIsExtensionInstalled(false);
    }
  }, []);

  return (
    <div
      className={`relative p-4 flex flex-col justify-between h-[100vh]  transition-[width] ease-in-out ${
        sidebarMini ? "w-[78px] sidebar__mini" : "w-[260px] sidebar__full"
      }`}
    >
      <div>
        <div className="flex justify-between items-center w-full">
          <div className="">
            <a
              href={`/${process.env.REACT_APP_APP_HOME}/${userData?.activeWorkspace?.slug}`}
              onClick={() => {
                captureEvent("TOP_PrimeRoleBtn");
              }}
              className="block mb-4"
            >
              {sidebarMini ? (
                <Image src={favicon} nobaseurl className="logo w-[28px] ml-2" alt="Logo" />
              ) : (
                <>
                  {appname === "PrimeRole" ? (
                    <>
                      {theme === "dark" ? (
                        <Image
                          src="/images/apps/primerole/logo-dark.svg"
                          className="logo no-invert w-[140px]"
                          alt="Logo"
                        />
                      ) : (
                        <Image src={logo} className="logo w-[140px]" alt="Logo" />
                      )}
                    </>
                  ) : (
                    <Image src={logo} className="logo w-[140px]" alt="Logo" />
                  )}
                </>
              )}
            </a>
          </div>
          <div>
            <div className="flex items-center w-full justify-between mb-4 sidebar__heading__wrapper">
              {/* <h3 className="font-medium text-base text-text-900 sidebar__heading">Settings</h3> */}
              <div
                className={`text-brand-600 font-normal bg-brand-100 rounded-full cursor-pointer ${sidebarMini && "relative left-6 z-40"}`}
              >
                {sidebarMini ? (
                  <ChevronRightCircle width={18} height={18} onClick={toggleSidebar} />
                ) : (
                  <ChevronLeftCircle width={18} height={18} onClick={toggleSidebar} />
                )}
              </div>
            </div>
          </div>
        </div>

        {userData?.workspaces?.data && (
          <div className="mb-3">
            <WorkspaceDropdown
              activeWorkspace={userData?.activeWorkspace}
              workspaces={userData?.workspaces?.data}
              switchWorkspace={switchWorkspace}
              sidebarMini={sidebarMini}
            />
          </div>
        )}

        {menuId !== "settings" &&
          menus.map((item) => {
            if (item.id === "settings") {
              return null;
            }
            return (
              <div key={item.name}>
                <a
                  className={
                    menuId && menuId === item?.id && !openSubMenus[item.id]
                      ? activeClassNames
                      : returnActiveClasses(location.pathname, item.id)
                  }
                  onClick={() => {
                    captureEvent("SET_SideNavBar_" + item.name + "Btn");
                    if (item.name === "Scoring") {
                      window.dispatchEvent(new Event("clearScoringRouteState"));
                    }
                    if (item.id === "settings" && item.hasSubMenu) {
                      openWorkspaceSettingsMenu();
                      return;
                    }
                    if (item.hasSubMenu) {
                      toggleSubMenu(item.id);
                    } else {
                      navigate(buildWorkspacePath(item.href));
                    }
                  }}
                >
                  {item.icon && (
                    <TooltipWrapper content={sidebarMini ? item.name : ""}>
                      <Image src={item.icon} alt="" width="20" className="mr-3 sidebar__item__menu__icon" />
                    </TooltipWrapper>
                  )}
                  <div className="flex-auto sidebar__item__menu__text">
                    <span className="block font-semibold">{item.name}</span>
                  </div>
                  {item.hasSubMenu && (
                    <>
                      {openSubMenus[item.id] ? (
                        <CaretUpIcon width={18} height={18} />
                      ) : (
                        <CaretRightIcon width={18} height={18} />
                      )}
                    </>
                  )}
                </a>
                {/* SubMenu toggle rendering */}
                {item.hasSubMenu && openSubMenus[item.id] && (
                  <div className="sidebar__sub__menu">
                    {item.subMenu?.map((sub) => (
                      <NavAnchor
                        key={sub.name}
                        to={sub.href}
                        activeClassName={`${activeClassNames} ml-7`}
                        className="sidebar__item__menu text-base text-text-700 my-1 group relative flex items-center rounded-lg py-2 px-3 ml-7 leading-6 hover:bg-secondary"
                        activePaths={sub.activePaths}
                        onClick={() => {
                          captureEvent("SET_SideNavBar_" + sub.name + "Btn");
                          if (sub.name === "Scoring") {
                            window.dispatchEvent(new Event("clearScoringRouteState"));
                          }
                        }}
                      >
                        {sub.icon && (
                          <TooltipWrapper content={sidebarMini ? sub.name : ""}>
                            <Image
                              src={sub.icon}
                              alt=""
                              width="20"
                              className="mr-3 sidebar__item__menu__icon"
                            />
                          </TooltipWrapper>
                        )}
                        <div className="flex-auto sidebar__item__menu__text">
                          <span className="block font-semibold">{sub.name}</span>
                        </div>
                      </NavAnchor>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        {hasSubMenu && subMenus && subMenus.length > 0 && menuId === "settings" && (
          <>
            <Button
              className={`mt-4 w-full text-xs ${sidebarMini && "mb-2"}`}
              variant="purple-outline"
              onClick={() => {
                setHasSubMenu(false);
                setSubMenus(null);
                setCurrentMenuTitle("");
                setMenuId("");
                setOpenSubMenus({});
              }}
            >
              <ChevronLeft width={16} height={16} className={`mr-1 `} />{" "}
              {!sidebarMini && <span>Back to Main Menu</span>}
            </Button>
            {currentMenuTitle && (
              <h3 className="font-medium text-base text-text-900 sidebar__heading mt-4 mb-3">
                {currentMenuTitle}
              </h3>
            )}
            {subMenus.map((item) => {
              if (item?.items?.length) {
                return (
                  <div key={`section-${item.sectionTitle}`} className="mt-0">
                    {item.sectionTitle && (
                      <h4 className="font-medium text-base text-text-900 sidebar__heading mt-3 mb-3">
                        {item.sectionTitle}
                      </h4>
                    )}
                    {item.items.map((link) => (
                      <NavAnchor
                        key={link.name}
                        to={`${link.href}`}
                        activeClassName={activeClassNames}
                        className={`sidebar__item__menu text-base text-text-700 my-1 group relative flex items-center rounded-lg py-2 px-3 leading-6 hover:bg-secondary`}
                        activePaths={link.activePaths}
                        onClick={() => {
                          captureEvent("SET_SideNavBar_" + link.name + "Btn");
                          if (link.name === "Scoring") {
                            window.dispatchEvent(new Event("clearScoringRouteState"));
                          }
                        }}
                      >
                        {link.icon && (
                          <TooltipWrapper content={sidebarMini ? link.name : ""}>
                            <Image
                              src={link.icon}
                              alt=""
                              width="20"
                              className="mr-3 sidebar__item__menu__icon"
                            />
                          </TooltipWrapper>
                        )}
                        <div className="flex-auto sidebar__item__menu__text">
                          <span className="block font-semibold ">{link.name}</span>
                        </div>
                      </NavAnchor>
                    ))}
                  </div>
                );
              }

              return (
                <NavAnchor
                  key={item.name}
                  to={`${item.href}`}
                  activeClassName={activeClassNames}
                  className={`sidebar__item__menu text-base text-text-700 my-1 group relative flex items-center rounded-lg py-2 px-3 leading-6 hover:bg-secondary`}
                  activePaths={item.activePaths}
                  onClick={() => {
                    captureEvent("SET_SideNavBar_" + item.name + "Btn");
                    if (item.name === "Scoring") {
                      window.dispatchEvent(new Event("clearScoringRouteState"));
                    }
                  }}
                >
                  {item.icon && (
                    <TooltipWrapper content={sidebarMini ? item.name : ""}>
                      <Image src={item.icon} alt="" width="20" className="mr-3 sidebar__item__menu__icon" />
                    </TooltipWrapper>
                  )}
                  <div className="flex-auto sidebar__item__menu__text">
                    <span className="block font-semibold ">{item.name}</span>
                  </div>
                </NavAnchor>
              );
            })}
          </>
        )}
        {menuId !== "settings" && (
          <div className="pt-2 border-t border-t-border mt-3">
            {/* <NavAnchor
              className={`sidebar__item__menu text-base text-text-700 my-1 group relative flex items-center rounded-lg py-2 px-3 leading-6 hover:bg-secondary`}
              asChild
              onClick={() => {
                captureEvent("HOM_InstallBtn");
              }}
              to={process.env.REACT_APP_CHROME_EXTENSION}
              target="_blank"
              slug={false}
            >
              <Image
                src="/images/icons/untitledui/chrome-extension-icon.svg"
                alt=""
                width="20"
                className="mr-3 sidebar__item__menu__icon"
              />
              <div className="flex-auto sidebar__item__menu__text inline-flex">
                <span className="block font-semibold ">Chrome Extension</span>
              </div>
              <Image src="/images/icons/untitledui/hyperlink.svg" alt="" width="14" />
            </NavAnchor> */}
            <NavAnchor
              className={`sidebar__item__menu text-base text-text-700 my-1 group relative flex items-center rounded-lg py-2 px-3 leading-6 hover:bg-secondary`}
              asChild
              onClick={() => {
                captureEvent("HOM_InstallBtn");
              }}
              to={`${process.env.REACT_APP_BASE_URL}/docs`}
              target="_blank"
              slug={false}
            >
              <TooltipWrapper content={sidebarMini ? "Resources" : ""}>
                <Image
                  style={{ maxWidth: "16px", minWidth: "17px", marginRight: "14px", marginLeft: "2px" }}
                  src="/images/icons/untitledui/resources-icon.svg"
                  alt=""
                  width="20"
                  className="mr-3 sidebar__item__menu__icon"
                />
              </TooltipWrapper>
              <div className="flex-auto sidebar__item__menu__text inline-flex">
                <span className="block font-semibold ">Resources</span>
              </div>
              {!sidebarMini && <Image src="/images/icons/untitledui/hyperlink.svg" alt="" width="14" />}
            </NavAnchor>
            <NavAnchor
              className={`sidebar__item__menu text-base text-text-700 my-1 group relative flex items-center rounded-lg py-2 px-3 leading-6 hover:bg-secondary`}
              asChild
              onClick={() => {
                captureEvent("HOM_InstallBtn");
              }}
              to={`mailto:support@primerole.com?cc=krishna@primerole.com&subject=I%20need%20help%20with%20PrimeRole&body=Please%20share%20feedback%2Fsuggestions%2Fbugs%20for%20the%20PrimeRole%20team%20`}
              target="_blank"
              slug={false}
            >
              <TooltipWrapper content={sidebarMini ? "Support" : ""}>
                <Image
                  // style={{ maxWidth: "16px", minWidth: "17px", marginRight: "14px", marginLeft: "2px" }}
                  src="/images/icons/untitledui/support-icon.svg"
                  alt=""
                  width="20"
                  className="mr-3 sidebar__item__menu__icon"
                />
              </TooltipWrapper>
              <div className="flex-auto sidebar__item__menu__text inline-flex">
                <span className="block font-semibold ">Support</span>
              </div>
              {!sidebarMini && <Image src="/images/icons/untitledui/hyperlink.svg" alt="" width="14" />}
            </NavAnchor>

            {/* <NavAnchor
            slug={false}
            key={"profile-settings"}
            to={`/profile`}
            activeClassName={activeClassNames}
            className={`sidebar__item__menu text-base text-text-700 my-1 group relative flex items-center rounded-lg py-2 px-3 leading-6 hover:bg-secondary`}
            activePaths={["/profile"]}
          >
            <Image
              src="/images/icons/untitledui/user-circle.svg"
              alt=""
              width="20"
              className="mr-3 sidebar__item__menu__icon"
            />
            <div className="flex-auto sidebar__item__menu__text">
              <span className="block font-semibold ">Profile Settings</span>
            </div>
          </NavAnchor> */}
          </div>
        )}
      </div>
      <div>
        {/* {isExtensionInstalled && (
          <div className="border border-brand-200 rounded-lg bg-brand-50 p-4">
            <Image src="/images/icons/untitledui/chrome-icon.svg" alt="" width="24" className="no-invert" />
            <Text size="sm" className="mt-2.5 mb-2">
              Install Chrome Extension
            </Text>
            <Text size="p" className="mb-3">
              Find contact details and view company intelligence on any website!
            </Text>
            <Button asChild>
              <a href={process.env.REACT_APP_CHROME_EXTENSION} target="_blank">
                Install <ExternalLink width={16} className="ml-1.5" />
              </a>
            </Button>
          </div>
        )}
        {pendingInvitations &&
          pendingInvitations.length > 0 &&
          !location?.pathname?.includes("/workspaces") && <InvitationsCard />} */}
        {!sidebarMini && menuId !== "settings" && (
          <div className="relative w-full max-w-sm mx-auto h-[220px] group">
            {isExtensionInstalled === false && (
              <motion.div
                initial={{ scale: pendingInvitations?.length > 0 ? 0.9 : 1 }}
                whileHover={{ scale: 1.02, zIndex: 11 }}
                onHoverStart={() => setIsExtensionHovered(true)}
                onHoverEnd={() => setIsExtensionHovered(false)}
                style={{ zIndex: 10 }}
                className="absolute -top-6 left-0 w-full"
              >
                <div className="border border-brand-200 rounded-xl bg-brand-50 p-4 shadow-md relative">
                  <Image
                    src="/images/icons/untitledui/chrome-icon.svg"
                    alt=""
                    width="24"
                    className="no-invert"
                  />
                  <Text size="sm" className="mt-2.5 mb-2">
                    Install Chrome Extension
                  </Text>
                  <Text size="p" className="mb-3">
                    Find contact details and view company intelligence on any website!
                  </Text>
                  <Button asChild>
                    <a
                      href={process.env.REACT_APP_CHROME_EXTENSION}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Install <ExternalLink width={16} className="ml-1.5" />
                    </a>
                  </Button>
                </div>
              </motion.div>
            )}

            {pendingInvitations?.length > 0 && !location?.pathname?.includes("/workspaces") && (
              <motion.div
                animate={{ scale: isExtensionHovered ? 0.9 : 1 }}
                transition={{ duration: 0.2 }}
                whileHover={{ scale: 1.02, zIndex: 11 }}
                style={{ zIndex: 10 }}
                className="absolute top-0 left-0 w-full"
              >
                <div className="relative z-10">
                  <InvitationsCard />
                </div>
              </motion.div>
            )}
          </div>
        )}

        {userData?.id && (
          <ProfileDropdown
            data={{
              name: userData?.name,
              icon: userData?.profile_photo_url,
              email: userData?.email,
            }}
            sidebarMini={sidebarMini}
          />
        )}
      </div>
    </div>
  );
}
